import { $wgsl } from "../../wgsl-preprocessor/wgsl-preprocessor";
import { UniformBuffer } from "../UniformBuffer";
import { initTexture } from "../Texture";
import { MipmapPipeline } from "./MipmapPipeline";
import materialUniforms from "./material_uniforms.wgsl";
import { getMaterialHash } from "../Pipelines";

function makeTextureBinding(binding = 0, visibility = GPUShaderStage.FRAGMENT, sampleType = "float") {
  return {
    binding,
    visibility,
    texture: {
      sampleType
    }
  };
}

function makeSamplerBinding(binding = 0, visibility = GPUShaderStage.FRAGMENT) {
  return {
    binding,
    visibility,
    sampler: {}
  };
}

export function getMaterialUniformsDeclaration(bindGroup = 0) {
  return $wgsl(materialUniforms, { bindGroup });
}

export const MaterialUniformFlags = {
  NO_UNIFORMS: 0x10000000,

  TEXTURE_MASK: 0x000000ff,
  MAP_DIFFUSE: 1,
  MAP_SPECULAR: 2,
  MAP_BUMP: 4,
  MAP_NORMAL: 8,
  MAP_ALPHA: 16
};

const _tmpTexMatrix = new Float32Array(9);

export class MaterialUniforms extends UniformBuffer {

  #device;
  #defaultTexture;
  #layout;
  #bindGroup;
  #mipmapPipeline;

  // Remember which resources we used on last createBindGroup() call, so
  // that we can detect if a texture was replaced. Indexed by binding.
  #boundResourceViews = [];

  constructor(device, material, defaultTexture) {
    // buffer size in floats
    let bufferSize = 40; // texture uniforms
    super(device, bufferSize, true, false, 'material-uniforms');

    this.#device = device;
    this.#defaultTexture = defaultTexture;

    this.#layout = device.createBindGroupLayout({
      label: "material bind group layout",
      entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.FRAGMENT | GPUShaderStage.VERTEX,
        buffer: {}
      },
      makeTextureBinding(1),
      makeSamplerBinding(2),
      makeTextureBinding(3),
      makeSamplerBinding(4),
      makeTextureBinding(5),
      makeSamplerBinding(6),
      makeTextureBinding(7),
      makeSamplerBinding(8)]

    });

    if (material) {
      this.#mipmapPipeline = new MipmapPipeline(device);
      this.initFromMaterial(material);
    } else {
      this.#initEmpty();
    }
  }

  #initEmpty() {

    let view = this.#defaultTexture.view;
    let smpl = this.#defaultTexture.sampler;

    this.#bindGroup = this.#device.createBindGroup({
      layout: this.#layout,
      entries: [
      {
        binding: 0,
        resource: {
          buffer: this.getBuffer()
        }
      },
      { binding: 1, resource: view },
      { binding: 2, resource: smpl },
      { binding: 3, resource: view },
      { binding: 4, resource: smpl },
      { binding: 5, resource: view },
      { binding: 6, resource: smpl },
      { binding: 7, resource: view },
      { binding: 8, resource: smpl }]

    });


  }

  #setTexTransforms(texture, uOffset) {

    let offset = texture.offset;
    let repeat = texture.repeat;

    if (texture.matrix) {
      _tmpTexMatrix.set(texture.matrix.elements);
    } else {
      for (let i = 0; i < 9; i++)
      _tmpTexMatrix[i] = i % 4 ? 0 : 1;
    }

    _tmpTexMatrix[6] += offset.x;
    _tmpTexMatrix[7] += offset.y;
    _tmpTexMatrix[0] *= repeat.x;
    _tmpTexMatrix[3] *= repeat.x;
    _tmpTexMatrix[1] *= repeat.y;
    _tmpTexMatrix[4] *= repeat.y;

    return this.setMatrix3x3IfChanged(uOffset, _tmpTexMatrix);
  }


  /**
   * Make sure that uniform buffer is up to date. We only upload on actual changes
   * to make sure that we only re-upload if the material changes really require it.
   * @param {THREE.Material} material
   * @returns {boolean} true if the uniform buffer was re-uploaded
   */
  updateUniformBuffer(material, forceUpload = false) {

    let needsUpload = false;

    let texForMatrix = material.map || material.specularMap;
    if (texForMatrix) {
      needsUpload |= this.#setTexTransforms(texForMatrix, 0);
    }

    let texForBumpMatrix = material.normalMap || material.bumpMap;
    if (texForBumpMatrix) {
      needsUpload |= this.#setTexTransforms(texForBumpMatrix, 12);
    }

    let texForAlphaMatrix = material.alphaMap;
    if (texForAlphaMatrix) {
      needsUpload |= this.#setTexTransforms(texForAlphaMatrix, 24);
    }

    if (material.bumpMap) {
      needsUpload |= this.setFloatIfChanged(36, material.bumpScale);
      needsUpload |= this.setFloatIfChanged(37, material.bumpScale);
    }

    if (material.normalMap) {
      needsUpload |= this.setFloatIfChanged(36, material.normalScale.x);
      needsUpload |= this.setFloatIfChanged(37, material.normalScale.y);
    }

    if (needsUpload || forceUpload) {
      this.upload();
    }

    return needsUpload;
  }

  initFromMaterial(material) {

    let mapsHash = 0;

    let entries = [
    {
      binding: 0,
      resource: {
        buffer: this.getBuffer()
      }
    }];


    this.#boundResourceViews.fill(null);

    const addMap = (name, bit, binding) => {
      let mapView, mapSampler;

      let texture = material[name];
      if (texture) {
        initTexture(this.#device, this.#mipmapPipeline, texture);
        mapView = texture.__gpuTextureView;
        mapSampler = texture.__gpuSampler;
        mapsHash |= bit;
      } else {
        mapView = this.#defaultTexture.view;
        mapSampler = this.#defaultTexture.sampler;
      }

      // track which texture views are used in the bindGroup
      this.#boundResourceViews[binding] = mapView;
      this.#boundResourceViews[binding + 1] = mapSampler;

      entries.push({ binding: binding, resource: mapView });
      entries.push({ binding: binding + 1, resource: mapSampler });
    };

    addMap("map", MaterialUniformFlags.MAP_DIFFUSE, 1);
    addMap("specularMap", MaterialUniformFlags.MAP_SPECULAR, 3);
    if (material.bumpMap) {
      addMap("bumpMap", MaterialUniformFlags.MAP_BUMP, 5);
    } else {
      addMap("normalMap", MaterialUniformFlags.MAP_NORMAL, 5);
    }
    addMap("alphaMap", MaterialUniformFlags.MAP_ALPHA, 7);

    this.updateUniformBuffer(material, true);

    const bindGroupDesc = {
      layout: this.#layout,
      entries
    };
    this.#bindGroup = this.#device.createBindGroup(bindGroupDesc);

    return mapsHash;
  }

  bindGroupNeedsUpdate(material) {

    // Detect if the bindGroup entry for a map type needs to change
    const mapEntryNeedsUpdate = (name, binding) => {
      // get TextureView that will be used for this map type
      const newTexture = material[name];
      const newTextureView = newTexture?.__gpuTextureView;
      const newSamplerView = newTexture?.__gpuSampler;

      // old TextureView
      const oldTextureView = this.#boundResourceViews[binding];
      const oldSamplerView = this.#boundResourceViews[binding + 1];

      // If any of the views will change, the bindGroup will change - which requires
      // an invalidation of any affected RenderBundles.
      return oldTextureView !== newTextureView || oldSamplerView !== newSamplerView;
    };

    const mapChanged = mapEntryNeedsUpdate("map", 1);
    const specularMapChanged = mapEntryNeedsUpdate("specularMap", 3);
    const bumpMapChanged = mapEntryNeedsUpdate("bumpMap", 5);
    const normalMapChanged = mapEntryNeedsUpdate("normalMap", 5);
    const alphaMapChanged = mapEntryNeedsUpdate("alphaMap", 7);

    return mapChanged || specularMapChanged || bumpMapChanged || normalMapChanged || alphaMapChanged;
  }

  getLayout() {
    return this.#layout;
  }

  getBindGroup() {
    return this.#bindGroup;
  }

}

export function getMaterialTextureMask(material) {

  let mask = 0;

  if (material.map) mask |= MaterialUniformFlags.MAP_DIFFUSE;
  if (material.specularMap) mask |= MaterialUniformFlags.MAP_SPECULAR;
  if (material.bumpMap) mask |= MaterialUniformFlags.MAP_BUMP;
  if (material.normalMap) mask |= MaterialUniformFlags.MAP_NORMAL;
  if (material.alphaMap) mask |= MaterialUniformFlags.MAP_ALPHA;

  return mask;
}

function materialDisposeHandler(event) {
  const material = event.target;
  if (material.__gpuMaterialUniforms) {
    material.__gpuMaterialUniforms.getBuffer().destroy();
    // TODO: Do we need to destroy more resources? Textures?
    material.__gpuMaterialUniforms = undefined;
  }

  material.removeEventListener('dispose', materialDisposeHandler);
}

// We need one per Renderer instance, because they use separate RenderBundles.
let materialTextureUpdateCallbacks = [];
export function addMaterialTextureUpdateCallback(callback) {
  materialTextureUpdateCallbacks.push(callback);
}

function onMaterialChanged(event) {

  const material = event.target;

  // Check if bindGroup needs to be updated.
  // Note that __gpuMaterialUniforms only exists if the material already had textures before.
  // So, if a material was used without textures before and a texture arrived, this check will not help.
  // We detect this case via the mask check below.
  const bindGroupNeedsUpdate = Boolean(material.__gpuMaterialUniforms?.bindGroupNeedsUpdate(material));

  // Check if material mask has changed (e.g. a texture was set on a formerly untextured material)
  const materialTextureMask = material.__gpuUniformsMask & MaterialUniformFlags.TEXTURE_MASK;
  const newMaterialTextureMask = getMaterialTextureMask(material);
  const maskChanged = materialTextureMask !== newMaterialTextureMask;

  // Check if the material change requires a new render pipeline.
  const hasTextures = newMaterialTextureMask !== 0;
  const oldMaterialHash = material.__gpuPipelineHash; // Note that getMateriaHash() already updates this value - so we have to get it first.
  const newMaterialHash = getMaterialHash(material, hasTextures);
  const materialHashChanged = oldMaterialHash !== newMaterialHash;

  if (materialHashChanged || bindGroupNeedsUpdate || maskChanged) {
    // Forward event to all callbacks (one per Renderer instance)

    // Note that this might be called quite often while loading the model.
    // Up to number of textures * number of materials that use the texture.
    // It would be better to disable render bundles while loading large textured models altogether,
    // but determining this and re-enabling render bundles after loading seems complex and invasive.
    for (const callback of materialTextureUpdateCallbacks) {
      callback(event);
    }
  } else {
    // If callbacks are not triggered, just make sure that the uniform buffer values keep up to date.
    // RenderBundles and Pipelines remain valid.
    //
    // Usually, material updates are handled in first initMaterialBindings() call after material modification.
    // However, when using RenderBundles, we don't iterate through individual shapes anymore.
    // So, we have to update the uniform buffer immediately here (if values changed).
    //
    // Remark: It's a bit inconsistent and not ideal to update directly in this case while deferring to next render otherwise.
    //         However, there's currently no simple way where to do instead. Also, when changing
    //         to indirect rendering, this may change soon anyway, so it's not worth major refactorings right now.
    const gpuUniforms = material.__gpuMaterialUniforms;
    if (gpuUniforms) {
      gpuUniforms.updateUniformBuffer(material);
    }
  }
}

export function initMaterialBindings(device, material, defaultTexture) {

  let mask = getMaterialTextureMask(material);

  if (mask !== 0) {
    if (material.__gpuMaterialUniforms) {
      // Reinitialize from material. In theory, we could also destroy the old MaterialUniforms
      // instance and create a new one. However, the UniformBuffer has always the same size for all
      // materials anyway. If we would destroy and recreate it, this would require invalidating all render bundles
      // that might still use it - even if the textures keep the same. Invalidating render bundles
      // is only done and necessary if the texture mask changed. (see materialTextureUpdateCallback below)
      material.__gpuMaterialUniforms.initFromMaterial(material);
    } else {
      const mu = new MaterialUniforms(device, material, defaultTexture);
      material.__gpuMaterialUniforms = mu;
      material.addEventListener('dispose', materialDisposeHandler);
    }
  }

  // Make sure callbacks are triggered to make sure affected RenderBundles are invalidated
  // whenever bindGroup resources are affected by a change.
  material.addEventListener('update', onMaterialChanged);

  material.__gpuUniformsMask = mask = mask | MaterialUniformFlags.NO_UNIFORMS;
  return mask;
}